import { useState, useEffect } from "react"
import { usePage } from "@inertiajs/react"
import { filmStyle } from "@/lib/films"
import { useLocalePath } from "@/lib/locale-path"
import type { PageProps } from "@/types/inertia"
import { BrandMotif } from "@/components/ui/brand-motif"

interface SiteCard {
  id: number
  name: string
  region: string
  primary_photo_url: string | null
  /** The location's own page — the rooms, the photos and the directions. */
  href: string
}

interface HeroSlide {
  photo_url: string
  alt_text: string | null
}

interface Props {
  slides: HeroSlide[]
  sites: SiteCard[]
  content: { eyebrow: string; subtext: string; therapy_cta: string; room_cta: string }
  /** The film over the slides — this section's own display setting, deep
   *  green by default. See @/lib/films. */
  film?: string | null
}

const FALLBACK_IMAGE = "/images/prestwich/oasis/O_5.jpg"
const SLIDE_INTERVAL = 5500

export default function HomeHero({ slides, sites, content, film }: Props) {
  const { translations } = usePage<PageProps>().props
  const t = translations.sanctuary_pages.home
  const lp = useLocalePath()
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    if (slides.length <= 1) return
    const timer = setInterval(() => {
      setActiveIndex(i => (i + 1) % slides.length)
    }, SLIDE_INTERVAL)
    return () => clearInterval(timer)
  }, [slides.length])

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden">
      {/* Background images — stacked, crossfade via opacity */}
      <div className="absolute inset-0">
        {slides.length > 0 ? (
          slides.map((slide, i) => (
            <img
              key={slide.photo_url}
              src={slide.photo_url}
              alt={slide.alt_text ?? ""}
              className="absolute inset-0 h-full w-full object-cover transition-opacity duration-1000"
              style={{ objectPosition: "center 35%", opacity: i === activeIndex ? 1 : 0 }}
              loading={i === 0 ? "eager" : "lazy"}
              decoding={i === 0 ? "sync" : "async"}
              fetchPriority={i === 0 ? "high" : "low"}
            />
          ))
        ) : (
          <img
            src={FALLBACK_IMAGE}
            alt={t.hero_fallback_alt}
            className="h-full w-full object-cover"
            style={{ objectPosition: "center 35%" }}
          />
        )}
        <div className="absolute inset-0" style={{ background: filmStyle(film) }} />
      </div>

      <BrandMotif variant="speckle" size={300} opacity={0.8} seed={4} className="absolute bottom-10 -right-10 hidden lg:block" />

      {/* Centre text */}
      <div className="relative mx-auto w-full max-w-4xl px-6 py-40 text-center lg:px-8">
        <p className="mb-4 text-sm font-medium uppercase tracking-[0.2em] text-white/60">
          {content.eyebrow}
        </p>
        <h1
          className="text-white"
          style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)", fontWeight: 300, lineHeight: 1.1 }}
        >
          Sanctuary Therapy Centre
        </h1>
        <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-white/75">
          {content.subtext}
        </p>
        <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <a
            href="/therapy-with-gisela"
            className="w-full rounded-full bg-white px-8 py-3 text-sm font-medium text-teal shadow transition-opacity hover:opacity-90 sm:w-auto"
          >
            {content.therapy_cta}
          </a>
          <a
            href="/rent-a-room"
            className="w-full rounded-full border border-white/40 px-8 py-3 text-sm font-medium text-white transition-colors hover:border-white hover:bg-white/10 sm:w-auto"
          >
            {content.room_cta}
          </a>
        </div>
      </div>

      {/* Two static site cards — desktop only. Each opens that location's
          own page, the same destination as the Our locations cards below. */}
      {sites.length > 0 && (
        <div className="absolute right-8 top-1/2 hidden -translate-y-1/2 flex-col gap-3 lg:flex xl:right-16">
          {sites.map(site => (
            <a
              key={site.id}
              href={lp(site.href)}
              className="block w-48 overflow-hidden rounded-2xl bg-white/95 shadow-xl backdrop-blur-sm no-underline transition-transform duration-200 hover:scale-[1.02]"
            >
              {site.primary_photo_url && (
                <img
                  src={site.primary_photo_url}
                  alt={site.name}
                  className="h-28 w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              )}
              <div className="p-3">
                <p className="text-[10px] font-medium uppercase tracking-wider text-teal">
                  {site.region}
                </p>
                <p className="mt-0.5 text-sm font-semibold text-ink-display">{site.name}</p>
                <p className="mt-2 text-xs font-medium text-teal-mid">{t.view_location}</p>
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Dot indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-8 left-1/2 flex -translate-x-1/2 gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className={`h-2 w-2 rounded-full transition-opacity duration-300 ${
                i === activeIndex ? "bg-white" : "bg-white/35 hover:bg-white/60"
              }`}
              aria-label={t.go_to_slide.replace("%{number}", String(i + 1))}
            />
          ))}
        </div>
      )}
    </section>
  )
}
